import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import api from '../services/api';

export function useUpload() {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | uploading | processing | done | error
  const [error, setError] = useState(null);
  const [isQuotaError, setIsQuotaError] = useState(false);
  const abortRef = useRef(null);

  const upload = useCallback(async (file, { description = '', eventId = '' } = {}, onComplete, onStarted, onFinished) => {
    setStatus('uploading');
    setProgress(0);
    setError(null);
    setIsQuotaError(false);
    let mediaId = null; // tracked outside try for cleanup in catch

    try {
      // Step 1: get signed POST policy from our backend
      const { data: signedData } = await api.post('/api/upload/signed-url', {
        fileType: file.type,
        fileName: file.name,
        fileSize: file.size,
        eventId,
      });

      const { signedUrl, fields } = signedData;
      mediaId = signedData.mediaId;

      // Notify parent so it can track this mediaId for cleanup on unload
      onStarted?.(mediaId);

      // Step 2: build FormData for GCS signed POST
      const formData = new FormData();
      // GCS signed POST fields must come BEFORE the file
      Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
      formData.append('file', file);

      // Step 3: upload directly to GCS (bypasses our server entirely)
      abortRef.current = new AbortController();
      await axios.post(signedUrl, formData, {
        signal: abortRef.current.signal,
        onUploadProgress: (e) => {
          setProgress(Math.round((e.loaded / e.total) * 100));
        },
        // Strip Authorization header — GCS doesn't want our JWT
        transformRequest: (data, headers) => {
          delete headers.Authorization;
          delete headers.common?.Authorization;
          return data;
        },
      });

      // Step 4: confirm upload with our backend → triggers processing queue
      setStatus('processing');
      await api.post(`/api/upload/confirm/${mediaId}`, { description });

      setStatus('done');
      onFinished?.(mediaId);
      onComplete?.({ mediaId, status: 'processing' });
    } catch (err) {
      if (axios.isCancel(err)) {
        setStatus('idle');
        return;
      }
      const isQuota = err.response?.status === 507 ||
        err.response?.data?.code === 'STORAGE_LIMIT_EXCEEDED';
      const message = err.response?.data?.error || err.message || 'Upload failed';
      setError(message);
      setIsQuotaError(isQuota);
      setStatus('error');
      onFinished?.(mediaId);
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setProgress(0);
    setError(null);
    setIsQuotaError(false);
  }, []);

  return { upload, cancel, reset, progress, status, error, isQuotaError };
}
